// src/pages/admin/GeneratePerformance.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../constants';

const GeneratePerformance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [viewType, setViewType] = useState('class'); // 'class' or 'level'
  const [formData, setFormData] = useState({
    class_uuid: '',
    class_level: '',
    session: '',
    terms: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/api/web/admin/performance-filters`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (data.success) {
          setClasses(data.classes || []);
          setSessions(data.sessions || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  // Get unique class levels from classes
  const classLevels = [...new Set(classes.map(c => c.class_level).filter(Boolean))].sort((a, b) => a - b);
  const levelNames = {
    1: 'JSS1',
    2: 'JSS2',
    3: 'JSS3',
    4: 'SS1',
    5: 'SS2',
    6: 'SS3'
  };

  const handleTermToggle = (term) => {
    setFormData(prev => ({
      ...prev,
      terms: prev.terms.includes(term)
        ? prev.terms.filter(t => t !== term)
        : [...prev.terms, term]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.session || formData.terms.length === 0) {
      alert('Please select session and at least one term');
      return;
    }

    if (viewType === 'class' && !formData.class_uuid) {
      alert('Please select a class');
      return;
    }

    if (viewType === 'level' && !formData.class_level) {
      alert('Please select a class level');
      return;
    }

    setLoading(true);
    const termsParam = formData.terms.join(',');
    const params = new URLSearchParams({
      session: formData.session,
      terms: termsParam
    });

    if (viewType === 'class') {
      params.append('class', formData.class_uuid);
    } else {
      params.append('level', formData.class_level);
    }

    navigate(`/students-performance?${params.toString()}`);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Generate Performance Report</h1>
        <p className="text-blue-100 mt-2">Select class, session and terms to view student performance</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              View Type *
            </label>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="viewType"
                  value="class"
                  checked={viewType === 'class'}
                  onChange={(e) => {
                    setViewType(e.target.value);
                    setFormData({ ...formData, class_uuid: '', class_level: '' });
                  }}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">Specific Class (e.g., JSS1A)</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="viewType"
                  value="level"
                  checked={viewType === 'level'}
                  onChange={(e) => {
                    setViewType(e.target.value);
                    setFormData({ ...formData, class_uuid: '', class_level: '' });
                  }}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">Class Level (e.g., All JSS1)</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {viewType === 'class' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Class *
                </label>
                <select
                  value={formData.class_uuid}
                  onChange={(e) => setFormData({ ...formData, class_uuid: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.uuid} value={cls.uuid}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Class Level *
                </label>
                <select
                  value={formData.class_level}
                  onChange={(e) => setFormData({ ...formData, class_level: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                  required
                >
                  <option value="">Select Class Level</option>
                  {classLevels.map(level => (
                    <option key={level} value={level}>
                      {levelNames[level] || `Level ${level}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session *
              </label>
              <select
                value={formData.session}
                onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                required
              >
                <option value="">Select Session</option>
                {sessions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Terms * (Select one or more)
              </label>
              <div className="flex gap-6">
                {['First Term', 'Second Term', 'Third Term'].map(term => (
                  <label key={term} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.terms.includes(term)}
                      onChange={() => handleTermToggle(term)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">{term}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneratePerformance;